import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { fetchArticles, fetchGalleries, createArticle, createGallery, deleteArticle, deleteGallery, uploadFiles } from '../services/apiService';
import { Article, GalleryItem } from '@/types';

interface AppContextType {
  articles: Article[];
  galleries: GalleryItem[];
  loading: boolean;
  error: string | null;
  addArticle: (article: Omit<Article, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  removeArticle: (id: number) => Promise<void>;
  addGallery: (gallery: Omit<GalleryItem, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  removeGallery: (id: number) => Promise<void>;
  uploadFiles: (files: File[]) => Promise<{ url: string }[]>;
  refreshData: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [galleries, setGalleries] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [articlesData, galleriesData] = await Promise.all([
        fetchArticles(),
        fetchGalleries(),
      ]);
      setArticles(articlesData);
      setGalleries(galleriesData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array means this function is created once

  useEffect(() => {
    loadData();
  }, []);

  const addArticle = async (article: Omit<Article, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newArticle = await createArticle(article);
      setArticles(prev => [...prev, newArticle]);
    } catch (err) {
      console.error('Error adding article:', err);
      throw err;
    }
  };

  const removeArticle = async (id: number) => {
    try {
      await deleteArticle(id);
      setArticles(prev => prev.filter(article => article.id !== id));
    } catch (err) {
      console.error('Error removing article:', err);
      throw err;
    }
  };

  const addGallery = async (gallery: Omit<GalleryItem, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newGallery = await createGallery(gallery);
      setGalleries(prev => [...prev, newGallery]);
    } catch (err) {
      console.error('Error adding gallery:', err);
      throw err;
    }
  };

  const removeGallery = async (id: number) => {
    try {
      await deleteGallery(id);
      setGalleries(prev => prev.filter(gallery => gallery.id !== id));
    } catch (err) {
      console.error('Error removing gallery:', err);
      throw err;
    }
  };

  const handleFileUpload = async (files: File[]) => {
    try {
      const uploadedFiles = await uploadFiles(files);
      return uploadedFiles;
    } catch (err) {
      console.error('Error uploading files:', err);
      throw err;
    }
  };

  return (
    <AppContext.Provider
      value={{
        articles,
        galleries,
        loading,
        error,
        addArticle,
        removeArticle,
        addGallery,
        removeGallery,
        uploadFiles: handleFileUpload,
        refreshData: loadData,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
